import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, PanelRightClose, PanelRightOpen } from "lucide-react";
import { useConfirm } from "@/contexts/ConfirmContext";

/**
 * Shared 100vh chat chrome for Agent/Writer/Creator. The caller owns messages,
 * sessionId and composer; this component just wraps the center/right columns
 * and manages the chat-history sidebar for the given `tool`.
 */
export default function ToolShell({
  brand,
  tool, // "agent" | "writer" | "creator"
  title,
  subtitle,
  Icon,
  sessionId,
  onLoadSession,
  onNewChat,
  refreshKey = 0, // bump after a successful send to refresh the sessions list
  emptyState, // ReactNode for the empty state (greeting + quick actions)
  isEmpty = true,
  children, // the scrolling message list when !isEmpty
  composer, // the floating composer
}) {
  const [sessions, setSessions] = useState([]);
  const [showRight, setShowRight] = useState(true);
  const scrollRef = useRef(null);
  const confirm = useConfirm();

  const refreshSessions = async () => {
    try {
      const { data } = await api.get(`/chat/sessions`, { params: { brand_id: brand.id, tool } });
      setSessions(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id, tool, refreshKey]);

  // Expose scroll ref for callers that want to auto-scroll on new messages.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [children]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Deze chat verwijderen?",
      body: "De berichten worden definitief verwijderd. Dit kan niet ongedaan worden gemaakt.",
      confirmLabel: "Verwijderen",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/chat/sessions/${sid}`, { params: { brand_id: brand.id, tool } });
      if (sid === sessionId) onNewChat?.();
      await refreshSessions();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  return (
    <div
      className="h-screen w-full flex bg-white text-slate-900 overflow-hidden"
      style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}
    >
      <div className="flex-1 flex flex-col min-w-0 relative">
        <header className="h-14 shrink-0 border-b border-slate-100 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-md bg-slate-900 text-white flex items-center justify-center">
              {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
            </div>
            <div className="truncate">
              <div className="text-sm font-medium text-slate-900 truncate">{title}</div>
              <div className="text-xs text-slate-500 truncate">{subtitle || brand.name}</div>
            </div>
          </div>
          <button
            onClick={() => setShowRight((s) => !s)}
            data-testid="toggle-history"
            className="hidden md:inline-flex items-center gap-2 h-8 px-3 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            {showRight ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            History
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto bh-scroll">
          {isEmpty ? (
            <div className="h-full flex items-center justify-center px-6">
              <div className="max-w-2xl w-full">{emptyState}</div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-4 lg:px-6 py-6 pb-40 space-y-5">{children}</div>
          )}
        </div>

        <div className="absolute left-0 right-0 bottom-4 px-4 lg:px-6 pointer-events-none">
          <div className="max-w-3xl mx-auto pointer-events-auto">{composer}</div>
        </div>
      </div>

      {showRight && (
        <aside className="hidden md:flex w-72 shrink-0 border-l border-slate-100 flex-col bg-slate-50/50">
          <div className="p-3">
            <button
              onClick={onNewChat}
              data-testid="new-chat-btn"
              className="w-full h-9 inline-flex items-center gap-2 justify-center rounded-md bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto bh-scroll px-2 pb-4">
            {sessions.length === 0 ? (
              <div className="text-xs text-slate-400 px-3 py-6 text-center">No chats yet</div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.session_id}
                  onClick={() => onLoadSession?.(s.session_id)}
                  data-testid={`session-${s.session_id}`}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-sm ${
                    s.session_id === sessionId
                      ? "bg-slate-200/70 text-slate-900"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <span className="flex-1 truncate">{s.title}</span>
                  <button
                    onClick={(e) => deleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-red-600 transition"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

/** A standard dark-primary pill-free composer used by Writer/Creator wrappers. */
export function Composer({ value, onChange, onSend, busy, placeholder, children }) {
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };
  return (
    <div className="rounded-2xl shadow-lg border border-slate-200 bg-white p-2">
      {children}
      <div className="flex items-end gap-2">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder}
          rows={1}
          className="flex-1 resize-none bg-transparent border-0 outline-none px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 max-h-40"
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          data-testid="composer-send-btn"
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

function SendIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}
